import { AppState, SubNavigation } from '../types/store';
import {
  PapiRequestDaBrowseNetworkbarDdDeviations,
  PapiUser,
} from '@wix/da-types-papi';
import { getDataEntityById } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import { userSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/user';
import { getMainNavNewContent } from '@wix/da-shared-react/pkg/NetworkBar/redux/selectors';
import {
  getIsLoggedIn,
  getIsMobile,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { BrowseLayout, ErrorNames, PAGE_STREAM_ID } from '../../constants';

export function getCurrentPageType(state: AppState) {
  return state.currentPageData?.pageType;
}

export function getSearchType(state: AppState) {
  return state.currentPageData?.searchType;
}

export function getCurrentFeedType(state: AppState) {
  return (
    getFeedTypeSubnavigation(state)?.currentValue ??
    state.currentPageData?.feedType
  );
}

export function getFeedTypeSubnavigation(
  state: AppState
): SubNavigation | undefined {
  return getSubnavigationByType(state, 'content_type');
}

export function getCurrentOrder(state: AppState) {
  return getOrderSubnavigation(state)?.currentValue ?? undefined;
}

export function getOrderSubnavigation(
  state: AppState
): SubNavigation | undefined {
  return getSubnavigationByType(state, 'order');
}

function getSubnavigationByType(
  state: AppState,
  type: string
): SubNavigation | undefined {
  return state.subnavs.find(subnav => subnav.type === type);
}

export function getThisGroup(state: AppState) {
  const groupId = state.currentPageData?.thisGroup;
  if (!groupId) {
    return;
  }

  return getDataEntityById<PapiUser, AppState>(state, userSchema, groupId);
}

export function getThisUser(state: AppState) {
  const userId = state.currentPageData?.thisUser;
  if (!userId) {
    return;
  }

  return getDataEntityById<PapiUser, AppState>(state, userSchema, userId);
}

export function getThisTopic(state: AppState) {
  return state.currentPageData?.thisNeco;
}

export function getTopTopics(state: AppState) {
  return getStripByType(state, 'rfy_topics', true)?.topNecos ?? [];
}

export function getThisTag(state: AppState) {
  return state.currentPageData?.thisTag;
}

export function getRelatedTopics(state: AppState) {
  return state.currentPageData?.relatedTopics;
}

export function getSearchQuery(state: AppState) {
  return state.currentPageData?.searchQuery;
}

export function getRelatedTags(state: AppState) {
  return getStripByType(state, 'related_tags', true)?.tags;
}
export function getWatchSuggestionsStrip(state: AppState) {
  return getStripByType(state, 'rfy_watch_suggestions');
}

export function getWatchSuggestions(state: AppState) {
  return getWatchSuggestionsStrip(state)?.watchSuggestions;
}

export function getWatchAllData(state: AppState) {
  // TODO: maybe there is a better way to do this...
  return getMainNavNewContent(state).find(item => item.key === 'watch');
}

export function getWatchedDeviantsStrip(state: AppState) {
  return getStripByType(state, 'dyw_deviants', true);
}

export function getWatchedDeviants(state: AppState) {
  return getWatchedDeviantsStrip(state)?.users ?? [];
}

export function getTopStrips(state: AppState) {
  return state.currentPageData?.topStrips?.filter(
    strip =>
      !['related_tags', 'dyw_deviants', 'rfy_topics'].includes(strip.codename)
  );
}

export function getCanSubmitToPostFeed(
  state: AppState,
  type: 'journal' | 'status' | 'poll'
) {
  // no postbox for logged out
  if (!getIsLoggedIn(state)) {
    return false;
  }

  // no journals on mobile
  if (type === 'journal' && getIsMobile(state)) {
    return false;
  }

  const pageType = getCurrentPageType(state);

  switch (pageType) {
    case 'watch':
      return !getThisUser(state);
    case 'groups':
      // groups only support journals
      if (type !== 'journal') {
        return false;
      }

      // check privs
      return !!getThisGroup(state)?.extendedGroup?.apps?.posts?.canSubmit;
    default:
      return true;
  }
}

export function getActiveUserId(state: AppState) {
  return state.currentPageData?.thisUser;
}

export function getStrips(state: AppState) {
  return state.currentPageData?.strips;
}

export function getStripByType(
  state: AppState,
  stripType: string,
  isTopStrip = false
) {
  if (isTopStrip) {
    return state.currentPageData?.topStrips?.find(
      strip => strip.codename === stripType
    );
  }

  return state.currentPageData?.strips?.find(
    strip => strip.codename === stripType
  );
}

export function getMainContentUrl(state: AppState) {
  // TODO: we do not have this returned by backend atm so we just hardcode deviations route
  // in the future, we are planning to rework the API for temporal sections and
  // should have it available again
  return getFeedTypeSubnavigation(state)?.options?.find(
    option => option.subnav === 'deviations'
  )?.url;
}

export function getSelectedTags(state: AppState) {
  if (state.currentPageData?.thisTag) {
    return [state.currentPageData.thisTag];
  }
}

const errorCodeMap = {
  0: ErrorNames.None,
  1: ErrorNames.Request,
  2: ErrorNames.HighOffset,
  3: ErrorNames.NoResults,
  4: ErrorNames.PastEndOfResults,
  5: ErrorNames.AllMature,
  6: ErrorNames.AllStored,
  7: ErrorNames.AllDeleted,
  8: ErrorNames.AllAntisocial,
  9: ErrorNames.HighLoad,
  10: ErrorNames.AllBlocked,
  11: ErrorNames.NoDeviantsYouWatch,
  742: ErrorNames.TagBlocked,
};

export function getErrorName(state: AppState): ErrorNames {
  const errorCode = state.currentPageData?.errorCode;

  if (errorCode === undefined) {
    return ErrorNames.None;
  }

  return errorCodeMap[errorCode] ?? ErrorNames.None;
}

export function getDailyDeviationDate(state: AppState) {
  return (streamSelectors.getStreamParam(
    state,
    PAGE_STREAM_ID,
    'requestParams'
  ) as PapiRequestDaBrowseNetworkbarDdDeviations)?.date;
}

export function getLayout(state: AppState): BrowseLayout {
  const layout = state.currentPageData?.layout ?? BrowseLayout.Torpedo;
  if (getIsMobile(state) && layout === BrowseLayout.Torpedo) {
    return BrowseLayout.Grid;
  }
  return layout;
}

export function getCanShowLessLikeThis(state: AppState) {
  return getCurrentPageType(state) === 'home' && getIsLoggedIn(state);
}
